import gql from 'graphql-tag';
import React, { FunctionComponent } from 'react';
import { Trans } from 'react-i18next';

import { AuctionTotalsFragment } from '../../../../types/__generated__';
import usePageQuery, {
  UsePageQueryResult,
  buildQuery,
} from '../../../hooks/usePageQuery';
import parseCurrency from '../../../utils/parseCurrency';
import classNames from 'classnames';
import AuctionTotalsSkeleton from './AuctionTotalsSkeleton';

const FRAGMENT = buildQuery(gql`
  fragment AuctionTotals on Page {
    targetWithCurrency {
      currencyCode
      value
    }
    donationSummary {
      aggregatedDonationsCount
      totalAmount {
        currencyCode
        value
      }
    }
    donations(first: 5) {
      totalCount
    }
  }
`);

interface AuctionTotalsProps {
  additionalIncome?: number;
}

const AuctionTotals: FunctionComponent<AuctionTotalsProps> = ({
  additionalIncome,
}) => {
  const result = usePageQuery<AuctionTotalsFragment>(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (result.loading) {
    return <AuctionTotalsSkeleton animated displayRefreshButton={false} />;
  }

  if (
    result.error &&
    !result.data?.donationSummary?.totalAmount &&
    !result.data?.targetWithCurrency?.value
  ) {
    return <AuctionTotalsSkeleton onRefresh={result.refetch} />;
  }

  if (!UsePageQueryResult.hasData(result)) {
    return null;
  }

  const { targetWithCurrency, donationSummary, donations } = result.data;

  const donationCurrency = donationSummary?.totalAmount?.currencyCode ?? 'GBP';
  const donationValue =
    donationSummary?.totalAmount?.value / 100 + (additionalIncome ?? 0) ?? 0;
  const targetAmount =
    targetWithCurrency?.value && targetWithCurrency.value > 0
      ? targetWithCurrency.value / 100
      : undefined;
  const targetCurrency = targetWithCurrency?.currencyCode || 'GBP';
  const directDonationCount =
    donations?.totalCount && donations.totalCount > 0
      ? donations.totalCount
      : undefined;
  const aggregatedDonationsCount =
    donationSummary?.aggregatedDonationsCount &&
    donationSummary?.aggregatedDonationsCount > 0
      ? donationSummary?.aggregatedDonationsCount
      : undefined;

  // console.log('Totals: ', additionalIncome);

  const donationTotalCount = aggregatedDonationsCount ?? directDonationCount;

  return (
    <figcaption>
      <div
        data-qa="totaliser-total"
        className={classNames('jg-h1', 'jg-text-color-branded')}
      >
        {parseCurrency(donationCurrency, donationValue, {
          roundDown: false,
          wholeNumber: true,
        })}
      </div>

      <div data-qa="totaliser-message" className={classNames('jg-text--light')}>
        <div>
          <Trans i18nKey="common.raised"> raised</Trans>
          {targetAmount && (
            <Trans i18nKey="page.totaliser_description">
              {' '}
              of{' '}
              <span
                className={classNames(
                  'jg-text--regular',
                  'jg-text-color-branded',
                )}
              >
                {{
                  targetAmount: parseCurrency(targetCurrency, targetAmount, {
                    roundDown: true,
                    wholeNumber: true,
                  }),
                }}
              </span>{' '}
              target
            </Trans>
          )}
        </div>
        {donationTotalCount && (
          <Trans i18nKey="page.totaliser_supporters" count={donationTotalCount}>
            {' '}
            by{' '}
            <span className={classNames('jg-text--regular')}>
              {{ donationsCount: donationTotalCount }} supporter
            </span>
          </Trans>
        )}
      </div>
    </figcaption>
  );
};

export default AuctionTotals;
